import React, { useState, useEffect, useRef } from 'react';

import api from '../../services/api';

import toast from '../../utils/toast';

import { useAuth } from '../../contexts/AuthContext';

import { ArrowDownTrayIcon, PhotoIcon, ChevronDownIcon, ChevronUpIcon } from '@heroicons/react/24/outline';



const COMPONENT_PREVIEW = {

  header: { left: 0, top: 0, width: 100, height: 29.7, coverWidth: true },

  stateStamp: { left: 0, top: 29.9, width: 34.4, height: 15.2 },

  signature: { left: 31.6, top: 30.4, width: 36.6, height: 14 },

  vendorSeal: { left: 67.5, top: 31.8, width: 31.6, height: 12.8 },

  serialNumber: { right: 3, top: 29.7 },

  dottedLine: { left: 6, top: 31, height: 69 },

};



const COMPONENT_SLOTS = [

  {

    key: 'header',

    label: 'Top Header Stamp',

    description: 'Crop to the ornate ₹500 header border only',

  },

  {

    key: 'stateStamp',

    label: 'State Stamp Area',

    description: 'Tamil Nadu label + circular stamp (left)',

  },

  {

    key: 'signature',

    label: 'Signature & Date',

    description: 'Handwritten date and signature (center)',

  },

  {

    key: 'vendorSeal',

    label: 'Stamp Vendor Seal',

    description: 'Vendor stamp (right, below serial number)',

  },

];



const PoaDocumentGenerator = () => {

  const { user } = useAuth();

  const [settings, setSettings] = useState(null);

  const [loading, setLoading] = useState(true);

  const [uploading, setUploading] = useState({});

  const [serialNumber, setSerialNumber] = useState('AX 026768');

  const [generating, setGenerating] = useState(false);

  const [showComponents, setShowComponents] = useState(false);

  const fileRefs = useRef({});



  useEffect(() => {

    loadSettings();

  }, []);



  const loadSettings = async () => {

    try {

      setLoading(true);

      const res = await api.get('/poa-generator/settings');

      setSettings(res.data.data);

    } catch (e) {

      console.error(e);

      toast.error('Failed to load POA generator settings');

    } finally {

      setLoading(false);

    }

  };



  const handleUpload = async (slot, file) => {

    if (!file) return;

    const formData = new FormData();

    formData.append('image', file);

    try {

      setUploading(prev => ({ ...prev, [slot]: true }));

      const res = await api.post(`/poa-generator/upload/${slot}`, formData, {

        headers: { 'Content-Type': 'multipart/form-data' },

      });

      setSettings(res.data.data);

      toast.success('Image uploaded');

    } catch (e) {

      toast.error(e.response?.data?.message || 'Upload failed');

    } finally {

      setUploading(prev => ({ ...prev, [slot]: false }));

    }

  };



  const handleGenerate = async () => {

    if (!serialNumber.trim()) {

      toast.error('Enter a serial number');

      return;

    }

    try {

      setGenerating(true);

      const res = await api.post(

        '/poa-generator/generate',

        { serialNumber: serialNumber.trim() },

        { responseType: 'blob' }

      );

      const blob = new Blob([res.data], { type: 'application/pdf' });

      const url = window.URL.createObjectURL(blob);

      const a = document.createElement('a');

      a.href = url;

      a.download = `POA_${serialNumber.trim().replace(/\s+/g, '_')}.pdf`;

      document.body.appendChild(a);

      a.click();

      a.remove();

      window.URL.revokeObjectURL(url);

      toast.success('PDF generated');

    } catch (e) {

      if (e.response?.data instanceof Blob) {

        const text = await e.response.data.text();

        try {

          const json = JSON.parse(text);

          toast.error(json.message || 'Generation failed');

        } catch {

          toast.error('Generation failed');

        }

      } else {

        toast.error(e.response?.data?.message || 'Generation failed');

      }

    } finally {

      setGenerating(false);

    }

  };



  if (user?.role !== 'admin') {

    return (

      <div className="p-6 text-center text-gray-500">Admin access required.</div>

    );

  }



  if (loading) {

    return (

      <div className="p-8 text-center text-gray-500">Loading...</div>

    );

  }



  const hasPdfTemplate = !!settings?.images?.pdfTemplate?.fileUrl;

  const hasTemplate = !!settings?.images?.fullPageTemplate?.fileUrl;

  const allComponents = COMPONENT_SLOTS.every(s => settings?.images?.[s.key]?.fileUrl);

  const canGeneratePdf = hasPdfTemplate || hasTemplate || allComponents;



  const renderUploadCard = (slotKey, label, description, tall = false) => {

    const img = settings?.images?.[slotKey];

    return (

      <div className="border border-gray-200 rounded-lg p-4">

        <h3 className="font-medium text-gray-900">{label}</h3>

        <p className="text-xs text-gray-500 mb-3">{description}</p>

        {img?.fileUrl ? (

          <div className={`mb-3 border rounded overflow-hidden bg-gray-50 ${tall ? '' : ''}`}>

            <img

              src={img.fileUrl}

              alt={label}

              className={`w-full object-contain ${tall ? 'max-h-80' : 'max-h-32'}`}

            />

            <p className="text-xs text-gray-400 px-2 py-1 truncate">{img.fileName}</p>

          </div>

        ) : (

          <div className={`mb-3 border-2 border-dashed border-gray-200 rounded flex items-center justify-center text-gray-400 ${tall ? 'h-48' : 'h-32'}`}>

            <PhotoIcon className="h-8 w-8" />

          </div>

        )}

        <input

          ref={el => { fileRefs.current[slotKey] = el; }}

          type="file"

          accept="image/png,image/jpeg,image/jpg,image/webp"

          className="hidden"

          onChange={e => handleUpload(slotKey, e.target.files?.[0])}

        />

        <button

          type="button"

          onClick={() => fileRefs.current[slotKey]?.click()}

          disabled={uploading[slotKey]}

          className="text-sm px-3 py-1.5 bg-indigo-600 text-white rounded hover:bg-indigo-700 disabled:opacity-50"

        >

          {uploading[slotKey] ? 'Uploading…' : img?.fileUrl ? 'Replace Image' : 'Upload Image'}

        </button>

      </div>

    );

  };



  return (

    <div className="p-6 max-w-5xl mx-auto space-y-8">

      <div>

        <h1 className="text-2xl font-bold text-gray-900">Generate POA Document</h1>

        <p className="text-sm text-gray-500 mt-1">

          Upload your Adobe Scan PDF for a pixel-perfect match. Only the serial number is replaced.

        </p>

      </div>



      {/* PDF template — best match */}

      <div className="bg-white shadow rounded-lg p-6 border-2 border-green-200">

        <div className="flex items-center gap-2 mb-1">

          <h2 className="text-lg font-semibold text-gray-800">PDF Template (Adobe Scan)</h2>

          <span className="text-xs bg-green-100 text-green-800 px-2 py-0.5 rounded-full font-medium">Recommended</span>

        </div>

        <p className="text-sm text-gray-500 mb-4">

          Upload the scanned stamp paper PDF directly. The original serial is masked and replaced while keeping layout, colours, and stamp sizes intact.

        </p>

        <div className="border border-gray-200 rounded-lg p-4">

          <h3 className="font-medium text-gray-900">Stamp Paper PDF</h3>

          <p className="text-xs text-gray-500 mb-3">Adobe Scan or similar — full page with existing serial visible</p>

          {hasPdfTemplate ? (

            <div className="mb-3 border rounded p-3 bg-gray-50">

              <p className="text-sm text-gray-800">{settings.images.pdfTemplate.fileName}</p>

              <a

                href={settings.images.pdfTemplate.fileUrl}

                target="_blank"

                rel="noopener noreferrer"

                className="text-xs text-indigo-600 hover:underline"

              >

                View uploaded PDF

              </a>

            </div>

          ) : (

            <div className="mb-3 h-20 border-2 border-dashed border-gray-200 rounded flex items-center justify-center text-gray-400 text-sm">

              No PDF uploaded

            </div>

          )}

          <input

            ref={el => { fileRefs.current.pdfTemplate = el; }}

            type="file"

            accept="application/pdf"

            className="hidden"

            onChange={e => handleUpload('pdfTemplate', e.target.files?.[0])}

          />

          <button

            type="button"

            onClick={() => fileRefs.current.pdfTemplate?.click()}

            disabled={uploading.pdfTemplate}

            className="text-sm px-3 py-1.5 bg-indigo-600 text-white rounded hover:bg-indigo-700 disabled:opacity-50"

          >

            {uploading.pdfTemplate ? 'Uploading…' : hasPdfTemplate ? 'Replace PDF' : 'Upload PDF'}

          </button>

        </div>

      </div>



      {/* Full page image template */}

      <div className="bg-white shadow rounded-lg p-6">

        <h2 className="text-lg font-semibold text-gray-800 mb-1">Image Template</h2>

        <p className="text-sm text-gray-500 mb-4">

          Alternative: upload a full-page PNG/JPG scan if you do not have a PDF.

        </p>

        {renderUploadCard(

          'fullPageTemplate',

          'Stamp Paper Scan',

          'Full A4 scan — header, stamps, signatures, blank body area.',

          true

        )}

        {hasTemplate && (

          <div className="mt-4 relative mx-auto border border-gray-300 shadow-inner max-w-md" style={{ aspectRatio: '595 / 842' }}>

            <img

              src={settings.images.fullPageTemplate.fileUrl}

              alt="Template preview"

              className="w-full h-full object-contain"

            />

            <div

              className="absolute font-mono"

              style={{

                right: '3%',

                top: '29.7%',

                fontSize: 11,

                color: '#D42222',

                fontFamily: 'Courier, monospace',

              }}

            >

              {serialNumber || 'AX 026768'}

            </div>

          </div>

        )}

      </div>



      {/* Generate */}

      <div className="bg-white shadow rounded-lg p-6">

        <h2 className="text-lg font-semibold text-gray-800 mb-4">Generate PDF</h2>

        <div className="flex flex-wrap items-end gap-4">

          <div>

            <label className="block text-sm font-medium text-gray-700 mb-1">

              Serial Number

            </label>

            <input

              type="text"

              value={serialNumber}

              onChange={e => setSerialNumber(e.target.value)}

              placeholder="AX 026768"

              className="border border-gray-300 rounded-md px-3 py-2 text-sm font-mono w-48"

              style={{ color: '#D42222' }}

            />

            <p className="text-xs text-gray-500 mt-1">

              Red Courier, top-right below header

            </p>

          </div>

          <button

            type="button"

            onClick={handleGenerate}

            disabled={!canGeneratePdf || generating}

            className="inline-flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 disabled:opacity-50 text-sm font-medium"

          >

            <ArrowDownTrayIcon className="h-4 w-4" />

            {generating ? 'Generating…' : 'Download PDF'}

          </button>

        </div>

        {!canGeneratePdf && (

          <p className="text-sm text-amber-600 mt-3">

            Upload a PDF template, a full-page image, or all four component images below.

          </p>

        )}

      </div>



      {/* Component mode — fallback */}

      <div className="bg-white shadow rounded-lg p-6">

        <button

          type="button"

          onClick={() => setShowComponents(!showComponents)}

          className="flex items-center gap-2 text-lg font-semibold text-gray-800 w-full text-left"

        >

          {showComponents ? <ChevronUpIcon className="h-5 w-5" /> : <ChevronDownIcon className="h-5 w-5" />}

          Component Images (Advanced)

        </button>

        {showComponents && (

          <div className="mt-4 space-y-4">

            <p className="text-sm text-gray-500">

              Use only if you cannot upload a full scan. Images are auto-trimmed on upload. Re-upload components after updating for best results.

            </p>

            {allComponents && !hasTemplate && (

              <div

                className="relative mx-auto border border-gray-300 shadow-inner"

                style={{ width: '100%', maxWidth: 420, aspectRatio: '595 / 842', background: '#EDE9DF' }}

              >

                <div

                  className="absolute border-l border-dashed border-stone-400/70"

                  style={{

                    left: `${COMPONENT_PREVIEW.dottedLine.left}%`,

                    top: `${COMPONENT_PREVIEW.dottedLine.top}%`,

                    height: `${COMPONENT_PREVIEW.dottedLine.height}%`,

                  }}

                />

                {COMPONENT_SLOTS.map(slot => {

                  const img = settings.images[slot.key];

                  const pos = COMPONENT_PREVIEW[slot.key];

                  return (

                    <img

                      key={slot.key}

                      src={img.fileUrl}

                      alt={slot.label}

                      className={`absolute ${pos.coverWidth ? 'object-cover' : 'object-cover'}`}

                      style={{

                        left: `${pos.left}%`,

                        top: `${pos.top}%`,

                        width: `${pos.width}%`,

                        height: pos.coverWidth ? 'auto' : `${pos.height}%`,

                        maxHeight: pos.coverWidth ? `${pos.height}%` : undefined,

                      }}

                    />

                  );

                })}

                <div

                  className="absolute font-mono"

                  style={{

                    right: `${COMPONENT_PREVIEW.serialNumber.right}%`,

                    top: `${COMPONENT_PREVIEW.serialNumber.top}%`,

                    fontSize: 11,

                    color: '#D42222',

                    fontFamily: 'Courier, monospace',

                  }}

                >

                  {serialNumber || 'AX 026768'}

                </div>

              </div>

            )}

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">

              {COMPONENT_SLOTS.map(slot => (

                <React.Fragment key={slot.key}>

                  {renderUploadCard(slot.key, slot.label, slot.description)}

                </React.Fragment>

              ))}

            </div>

          </div>

        )}

      </div>



      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 text-xs text-blue-900">

        <p className="font-medium mb-1">Why PDF template works best</p>

        <p>

          Your Adobe Scan PDF keeps the original layout perfectly. The app masks the old serial and draws the new number in the same position.

          Image or component uploads are fallbacks and may not match as closely.

        </p>

      </div>

    </div>

  );

};



export default PoaDocumentGenerator;

